import { useMemo } from 'react';

import useAppContext from 'flow/hooks/useAppContext';
import { useAppSelector } from 'flow/hooks/useRedux';
import {
    makeSelectContactSearchResultKeys,
    selectConnectedServicesAreOffered,
    selectExternalContactKeysSortedByName,
    selectExtensionKeysByDepartment,
    selectFavoriteContactKeys,
    selectHasSyncedSocialIntegrationAccount,
    selectSocialIntegrationContactKeysSortedByName,
} from 'flow/selectors/contact-list-selectors';
import { selectOwnExtensionKey } from 'flow/selectors/ExtensionSelectors';
import { getDepartmentState } from 'flow/utils/browser-utils';
import { buildContactListRows } from './contact-list-rows';

interface Options {
    searchText: string;
    showOverviewRow: boolean;
    sectionOpenStates: Record<string, boolean>;
}

const useContactListRows = ({
    searchText,
    showOverviewRow,
    sectionOpenStates,
}: Options) => {
    const { i18next } = useAppContext();

    const ownExtensionKey = useAppSelector(selectOwnExtensionKey);
    const extensionKeysByDepartment = useAppSelector(
        selectExtensionKeysByDepartment,
    );
    const externalContactKeys = useAppSelector(
        selectExternalContactKeysSortedByName,
    );
    const thirdPartyContactKeys = useAppSelector(
        selectSocialIntegrationContactKeysSortedByName,
    );
    const favoriteKeys = useAppSelector(selectFavoriteContactKeys);
    const selectContactSearchResultKeys = useMemo(
        makeSelectContactSearchResultKeys,
        [],
    );
    const searchResultKeys = useAppSelector((state) =>
        selectContactSearchResultKeys(state, searchText),
    );
    const connectedServicesAreOffered = useAppSelector(
        selectConnectedServicesAreOffered,
    );
    const hasSyncedSocialIntegrationAccount = useAppSelector(
        selectHasSyncedSocialIntegrationAccount,
    );

    return useMemo(
        () =>
            buildContactListRows({
                searchText,
                showOverviewRow,
                ownExtensionKey,
                extensionKeysByDepartment,
                externalContactKeys,
                thirdPartyContactKeys,
                favoriteKeys,
                searchResultKeys,
                connectedServicesAreOffered,
                hasSyncedSocialIntegrationAccount,
                sectionIsOpen: (sectionId) =>
                    sectionOpenStates[sectionId] ??
                    getDepartmentState(sectionId),
                translate: (key) => i18next.t(key),
            }),
        [
            searchText,
            showOverviewRow,
            ownExtensionKey,
            extensionKeysByDepartment,
            externalContactKeys,
            thirdPartyContactKeys,
            favoriteKeys,
            searchResultKeys,
            connectedServicesAreOffered,
            hasSyncedSocialIntegrationAccount,
            i18next,
            sectionOpenStates,
        ],
    );
};

export default useContactListRows;
